import { useEffect, useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import apiService from '../../services/api';

function clientLabel(client) {
  const name = [client.prenom, client.nom].filter(Boolean).join(' ').trim();
  return name ? `${name} (${client.email})` : client.email || client.id;
}

export default function PromotionClientPicker({
  commercantId,
  selectedClients = [],
  onChange,
  disabled = false,
}) {
  const [query, setQuery] = useState('');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!commercantId) {
      setClients([]);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError('');

    (async () => {
      try {
        const res = await apiService.request(
          `/commercants/${commercantId}/clients?page=1&pageSize=50`
        );
        const list = Array.isArray(res) ? res : res?.data || res?.clients || [];
        if (!cancelled) setClients(list);
      } catch (e) {
        if (!cancelled) {
          setClients([]);
          setLoadError(e?.message || 'Impossible de charger les clients');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [commercantId]);

  const selectedIds = useMemo(
    () => new Set(selectedClients.map((c) => c.id || c.email)),
    [selectedClients]
  );

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    return clients
      .filter((c) => {
        const key = clientLabel(c).toLowerCase();
        return key.includes(q) || (c.email || '').toLowerCase().includes(q);
      })
      .filter((c) => !selectedIds.has(c.id) && !selectedIds.has(c.email))
      .slice(0, 8);
  }, [clients, query, selectedIds]);

  const addClient = (client) => {
    onChange([
      ...selectedClients,
      {
        id: client.id,
        email: client.email,
        label: clientLabel(client),
      },
    ]);
    setQuery('');
  };

  const removeClient = (entry) => {
    onChange(
      selectedClients.filter(
        (c) => (c.id || c.email) !== (entry.id || entry.email)
      )
    );
  };

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
        <input
          type="search"
          value={query}
          disabled={disabled || !commercantId}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={
            commercantId
              ? 'Rechercher un client (nom, email)…'
              : 'Sélectionnez d’abord un commerçant'
          }
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      {loading && <p className="text-xs text-gray-500">Chargement des clients…</p>}
      {loadError && <p className="text-xs text-red-600">{loadError}</p>}

      {suggestions.length > 0 && (
        <ul className="border border-gray-200 rounded-lg divide-y divide-gray-100 bg-white shadow-sm max-h-48 overflow-y-auto">
          {suggestions.map((client) => (
            <li key={client.id}>
              <button
                type="button"
                onClick={() => addClient(client)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
              >
                {clientLabel(client)}
              </button>
            </li>
          ))}
        </ul>
      )}

      {selectedClients.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedClients.map((entry) => (
            <span
              key={entry.id || entry.email}
              className="inline-flex items-center gap-1 rounded-full bg-violet-100 text-violet-900 px-2.5 py-1 text-xs font-medium"
            >
              {entry.label || entry.email}
              <button
                type="button"
                onClick={() => removeClient(entry)}
                className="hover:text-violet-700"
                aria-label="Retirer"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <p className="text-xs text-gray-500">
        Seuls les clients sélectionnés pourront utiliser ce code promo.
      </p>
    </div>
  );
}
